import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { farmKeys } from '../queries/keys';
import { farmPaths } from '../queries/paths';

// TSS vite/03 — server-state hooks for farms (api-reference/admin-farms.md).
// owner_id (FK → clients) is required on create.

// Página por defecto del listado (contrato: limit def 50 / máx 500).
const LIST_LIMIT = 50;

export function useFarmsList(filters) {
    return useQuery({
        queryKey: farmKeys.list(filters),
        queryFn:  ({ signal }) => api.get(farmPaths.list(), { params: filters, signal }).then(r => r.data),
        placeholderData: (prev) => prev,
    });
}

// Listado paginado "cargar más" (offset). Páginas acumuladas en TanStack Query —
// vite/03 [RIGID]. Reemplaza el limit:500 + aviso de truncado del overview.
export function useFarmsInfinite(filters, limit = LIST_LIMIT) {
    return useInfiniteQuery({
        queryKey: farmKeys.infinite(filters, limit),
        queryFn:  ({ pageParam, signal }) =>
            api.get(farmPaths.list(), { params: { ...filters, limit, offset: pageParam }, signal }).then(r => r.data),
        initialPageParam: 0,
        getNextPageParam: (last) => {
            const p = last?.pagination;
            if (!p) return undefined;
            const next = p.offset + p.limit;
            return next < p.total ? next : undefined;
        },
        placeholderData: (prev) => prev,
    });
}

export function useFarm(id) {
    return useQuery({
        queryKey: farmKeys.detail(id),
        queryFn:  ({ signal }) => api.get(farmPaths.detail(id), { signal }).then(r => r.data),
        enabled:  !!id && id !== 'new',
    });
}

export function useCreateFarm() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(farmPaths.list(), payload).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: farmKeys.all }),
    });
}

export function useUpdateFarm(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(farmPaths.detail(id), payload).then(r => r.data),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: farmKeys.detail(id) });
            qc.invalidateQueries({ queryKey: farmKeys.all });
        },
    });
}

export function useDeleteFarm() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api.delete(farmPaths.detail(id)).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: farmKeys.all }),
    });
}
